import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, Phone, Calendar, Shield, Users, TrendingUp, 
  ArrowRight, Zap, Database, MessageSquare, Layers, FileText, 
  Smartphone, Sparkles, Star, CheckCircle2, Heart 
} from 'lucide-react';

// Assets
import eliseAvatar from '../assets/elise-avatar.png';
// Using public paths for consistent image loading as seen in other files or absolute paths if needed.
// Based on AgentsSection.jsx, they use /assets/name.png.

const FrontOfficeSuitePage = () => {
  const [activeTab, setActiveTab] = useState('front-office');

  const agents = [
    {
      name: 'Amy',
      role: 'Receptionist',
      description: 'Handles inbound calls 24/7 with zero patient wait times. Schedules directly into Asha PMS.',
      image: '/assets/amy.png',
      link: '/agents/receptionist',
      skills: ['Inbound Calls', 'Scheduling', 'Q&A']
    },
    {
      name: 'Leena',
      role: 'Schedule Optimizer',
      description: 'Prevents no-shows and fills cancellations with new patients to maximize production.',
      image: '/assets/leena.png',
      link: '/agents/schedule-optimizer',
      skills: ['Schedule Filling', 'Confirmation', 'Recall']
    },
    {
      name: 'Stephanie',
      role: 'Hygiene Recall',
      description: 'Proactively calls unscheduled patients to book their hygiene appointments.',
      image: '/assets/stephanie.png',
      link: '/agents/hygiene-recall',
      skills: ['Outbound Calls', 'Reactivation', 'Booking']
    },
    {
      name: 'Amanda',
      role: 'Insurance Verification',
      description: 'Automatically verifies patient insurance benefits and eligibility hours before visits.',
      image: '/assets/amanda.png',
      link: '/agents/insurance-verification',
      skills: ['Verification', 'Benefits', 'Eligibility']
    },
    {
      name: 'Kim',
      role: 'Case Acceptance',
      description: 'Helps patients understand treatment plans and move forward with care.',
      image: '/assets/kim.png',
      link: '/agents/case-acceptance',
      skills: ['Treatment Pres.', 'Education', 'Sales']
    },
    {
      name: 'Anya',
      role: 'Marketing Leads',
      description: 'Qualifies and nurtures new patient leads from marketing campaigns instantly.',
      image: '/assets/anya.png',
      link: '/agents/marketing-coordinator',
      skills: ['Lead Qual.', 'Nurturing', 'Conversion']
    },
    {
      name: 'Elise',
      role: 'Patient Experience',
      description: 'Follows up with patients to ensure satisfaction and generate 5-star reviews.',
      image: '/assets/elise.png',
      link: '/agents/patient-experience',
      skills: ['Feedback', 'Reviews', 'Follow-up']
    },
    {
      name: 'Joseph',
      role: 'Smart Faxes',
      description: 'Auto-sorts incoming faxes and attaches them to patient files in Asha PMS.',
      image: 'https://framerusercontent.com/images/6izWh5ISgcBrcePDm6DcMGOOIA.png', // Fallback or generic
      skills: ['Sorting', 'Filing', 'PMS Sync']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                <span>Complete Front Office Automation</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
                <span className="text-gray-900">The Only Platform with</span><br />
                <span className="text-blue-600">8 AI Agents</span>
              </h1>
              
              <p className="text-lg text-gray-700 leading-relaxed max-w-lg">
                Other platforms offer isolated tools. We provide a coordinated team of 8 AI agents that work together to automate your entire front office—from calls and scheduling to insurance and marketing.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <button className="px-6 py-3 rounded-xl font-bold text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300 transform hover:-translate-y-0.5">
                    Book a Demo
                  </button>
                </Link>
                <button className="px-6 py-3 rounded-xl font-bold text-sm transition-all bg-white text-gray-700 hover:bg-gray-50 border border-gray-200">
                  See Pricing
                </button>
              </div>

            </div>
            
            {/* Right: Interactive Visual - Team Grid */}
            <div className="relative flex items-center justify-center">
              <div className="relative w-full max-w-lg">
                 {/* Decorative background blobs */}
                 <div className="absolute -top-10 -right-10 w-64 h-64 bg-purple-200/40 rounded-full blur-3xl"></div>
                 <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-blue-200/40 rounded-full blur-3xl"></div>

                 {/* Main Grid Card */}
                 <div className="glass-card rounded-3xl p-6 border border-white/60 shadow-xl relative z-10">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Your Front Office Team</h3>
                            <p className="text-xs text-gray-500">8 Active Agents • 24/7 Coverage</p>
                        </div>
                        <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                            ONLINE
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {agents.map((agent, i) => (
                            <div key={i} className="flex flex-col items-center group cursor-pointer">
                                <div className="relative mb-2">
                                    <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-sm ring-2 ring-white group-hover:ring-blue-400 transition-all transform group-hover:scale-105">
                                        <img src={agent.image} alt={agent.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
                                </div>
                                <span className="text-xs font-bold text-gray-800">{agent.name}</span>
                                <span className="text-[9px] text-gray-500 uppercase tracking-wide text-center leading-tight mt-0.5">{agent.role.split(' ')[0]}</span>
                            </div>
                        ))}
                    </div>

                    {/* Active Action Toast */}
                    <div className="mt-6 bg-white rounded-xl p-3 shadow-md border border-gray-100 flex items-center gap-3 animate-float">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-gray-900">Amy just booked a new patient</p>
                            <p className="text-[10px] text-gray-500 truncate">Migrating Dentrix data...</p>
                        </div>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agents Grid Section - Styled like AgentsSection.jsx */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
               Meet Your New <span className="text-blue-600">Front Office Team</span>
            </h2>
            <p className="text-lg text-gray-600">
               Each agent is a specialist in their field, but they work together seamlessly to provide a complete automation solution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {agents.map((agent, index) => (
              <Link 
                to={agent.link || '#'} 
                key={index}
                className={`group relative bg-white rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300 overflow-hidden flex flex-col h-full ${!agent.link ? 'cursor-default' : 'cursor-pointer'}`}
              >
                 <div className="p-5 pb-0 flex items-start justify-between">
                    <div className="relative">
                      <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-sm ring-1 ring-slate-100">
                          <img src={agent.image} alt={agent.name} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-green-500 border-[3px] border-white w-7 h-7 rounded-full" title="Online" />
                    </div>
                    {agent.link && (
                      <div className="w-8 h-8 rounded-full bg-slate-50 group-hover:bg-blue-600 flex items-center justify-center transition-colors duration-300">
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                      </div>
                    )}
                  </div>

                  <div className="p-5 flex-1 flex flex-col">
                    <div className="mb-3">
                      <h3 className="text-xl font-bold text-slate-900 mb-0.5">{agent.name}</h3>
                      <p className="text-sm font-bold text-blue-600 uppercase tracking-wide">{agent.role}</p>
                    </div>
                    
                    <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-1">
                      {agent.description}
                    </p>

                    <div className="flex flex-wrap gap-1.5 mt-auto">
                      {agent.skills?.map((skill, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-md bg-slate-50 text-slate-600 text-[11px] font-bold border border-slate-100 uppercase tracking-wider">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="h-1 w-full bg-gradient-to-r from-blue-500 to-indigo-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section - The Asha Difference */}
      <section className="py-24 px-6 bg-gray-50 border-t border-gray-100">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-16 items-center">
             <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider mb-6">
                    Why We Are Different
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Integrated Intelligence vs. <br/> <span className="text-red-500">Isolated Tools</span>
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Most platforms sell you a "receptionist" or a "reminder tool". These isolated agents can't talk to each other, creating data silos and disjointed patient experiences.
                </p>
                
                <div className="space-y-6">
                    <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm flex-shrink-0">
                            <Layers className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">Unified Workflow</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Our agents share a brain. When Amy books an appointment, Amanda instantly verifies insurance, and Leena optimizes the slot.
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm flex-shrink-0">
                            <Database className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">No Data Entry</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Everything lives in Asha after migration. You don't have to copy-paste data back into a legacy PMS.
                            </p>
                        </div>
                    </div>
                </div>
             </div>

             <div className="relative">
                {/* Visual Comparison Card */}
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500"></div>
                    
                    <div className="grid grid-cols-1 gap-8">
                        {/* Scenario */}
                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Patient Scenario</h4>
                            <div className="flex gap-3">
                                <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 overflow-hidden">
                                     <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1888&auto=format&fit=crop" alt="Patient" className="w-full h-full object-cover" />
                                </div>
                                <div className="bg-white p-3 rounded-tr-xl rounded-b-xl shadow-sm text-sm text-gray-700 border border-gray-200">
                                    "I need to reschedule my cleaning for next Tuesday, but I have new insurance."
                                </div>
                            </div>
                        </div>

                        {/* Asha Response */}
                        <div className="relative">
                            <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-1 h-12 bg-green-500 rounded-r"></div>
                            <h4 className="text-sm font-bold text-green-600 uppercase tracking-wide mb-4 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                Asha Platform Response
                            </h4>
                            
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-xl border border-blue-100">
                                    <img src="/assets/amy.png" className="w-8 h-8 rounded-full object-cover" alt="Amy" />
                                    <div className="flex-1">
                                        <p className="text-xs font-bold text-gray-900">Amy (Receptionist)</p>
                                        <p className="text-xs text-gray-600">Finds slot Tue @ 2pm</p>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-gray-400" />
                                </div>

                                <div className="flex items-center gap-3 bg-purple-50 p-3 rounded-xl border border-purple-100 ml-4">
                                    <img src="/assets/amanda.png" className="w-8 h-8 rounded-full object-cover" alt="Amanda" />
                                    <div className="flex-1">
                                        <p className="text-xs font-bold text-gray-900">Amanda (Insurance)</p>
                                        <p className="text-xs text-gray-600">Verifies new policy instantly</p>
                                    </div>
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                </div>

                                <div className="flex items-center gap-3 bg-green-50 p-3 rounded-xl border border-green-100 ml-8">
                                    <img src="/assets/leena.png" className="w-8 h-8 rounded-full object-cover" alt="Leena" />
                                    <div className="flex-1">
                                        <p className="text-xs font-bold text-gray-900">Leena (Schedule)</p>
                                        <p className="text-xs text-gray-600">Fills the empty slot</p>
                                    </div>
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* Deep PMS Integration Section */}
      <section className="py-24 px-6 relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
            <div className="grid md:grid-cols-2 gap-16 items-center">
                <div>
                   <div className="inline-block bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-6">
                      SEAMLESS MIGRATION
                   </div>
                   <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                      Seamless PMS Migration + <br/> Communication Suite
                   </h2>
                   <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                      We migrate your schedule, patient records, and communications onto Asha so your front office can run in one system.
                   </p>
                   
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700 hover:bg-slate-800 transition-colors">
                          <Database className="w-8 h-8 text-blue-400 mb-4" />
                          <h4 className="font-bold text-lg mb-2">Live on Asha</h4>
                          <p className="text-sm text-slate-400">Appointments, patient records, and messaging run natively after migration.</p>
                      </div>
                      <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700 hover:bg-slate-800 transition-colors">
                          <Smartphone className="w-8 h-8 text-purple-400 mb-4" />
                          <h4 className="font-bold text-lg mb-2">Comms Suite</h4>
                          <p className="text-sm text-slate-400">Includes VoIP, 2-Way Texting, and Mass Messaging.</p>
                      </div>
                   </div>
                </div>

                <div className="bg-slate-800 rounded-3xl p-8 border border-slate-700 shadow-2xl relative">
                    <div className="absolute top-4 right-4 flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
                    </div>
                    
                    <div className="mt-4 space-y-6">
                        {/* PMS Logos Row */}
                        <div className="grid grid-cols-2 gap-4">
                            {['Dentrix', 'Eaglesoft', 'Open Dental', 'Curve'].map((pms, i) => (
                                <div key={i} className="bg-slate-900/50 h-12 rounded-lg flex items-center justify-center border border-slate-700/50 text-slate-500 font-bold text-sm">
                                    {pms}
                                </div>
                            ))}
                        </div>
                        
                        {/* Connection Visual */}
                        <div className="relative h-20 flex items-center justify-center">
                            <div className="w-full h-px bg-slate-700 absolute"></div>
                            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-full text-xs font-bold relative z-10 flex items-center gap-2 shadow-lg shadow-blue-900/50">
                                <Zap className="w-3 h-3 fill-current" />
                                MIGRATION COMPLETE
                            </div>
                        </div>

                        {/* Asha Suite */}
                        <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-white" />
                                </div>
                                <span className="font-bold">Asha Dashboard</span>
                            </div>
                            <div className="space-y-2">
                                <div className="h-2 bg-slate-800 rounded-full w-3/4"></div>
                                <div className="h-2 bg-slate-800 rounded-full w-1/2"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FrontOfficeSuitePage;





























